import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CaretUpDown, Check, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import NumberInput from "@/components/ui/NumberInput";

/**
 * Combobox buscable de productos o insumos.
 * Permite crear un nuevo item al vuelo desde el mismo popover.
 *
 * Props:
 *  - kind: "productos" | "insumos"
 *  - value: { codigo, nombre } — item actualmente seleccionado
 *  - onSelect: (item) => void  — devuelve { codigo, nombre, precio_actual, unidad }
 *  - placeholder?: string
 *  - testid?: string
 */
export default function ProductoCombobox({ kind, value, onSelect, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({ nombre: "", codigo: "", precio_actual: "", unidad: "", iva_pct: "" });

  const endpoint = kind === "insumos" ? "/insumos" : "/productos";
  const label = kind === "insumos" ? "insumo" : "producto";
  const defaultUnidad = kind === "insumos" ? "unidad" : "kg";
  const defaultIvaPct = kind === "insumos" ? 10.5 : 10.5;

  const load = async () => {
    const { data } = await api.get(endpoint);
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items.slice(0, 100);
    const q = query.toLowerCase();
    return items
      .filter((p) =>
        (p.codigo || "").toLowerCase().includes(q) ||
        (p.nombre || "").toLowerCase().includes(q)
      )
      .slice(0, 100);
  }, [items, query]);

  const displayLabel = () => {
    if (value?.nombre) {
      return (
        <span className="flex items-center gap-2 truncate">
          {value.codigo && <span className="text-[10px] text-[#6B1F24] font-mono">{value.codigo}</span>}
          <span className="truncate">{value.nombre}</span>
        </span>
      );
    }
    return <span className="text-[#999]">{placeholder || "Buscar por código o nombre…"}</span>;
  };

  const openCreate = () => {
    setNewItem({
      nombre: query.trim(),
      codigo: "",
      precio_actual: "",
      unidad: defaultUnidad,
      iva_pct: String(defaultIvaPct),
    });
    setCreating(true);
    setOpen(false);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    const nombre = newItem.nombre.trim();
    if (!nombre) return toast.error("Ingresá un nombre");
    setSaving(true);
    try {
      const payload = {
        nombre,
        codigo: newItem.codigo.trim() || undefined,
        precio_actual: parseFloat(newItem.precio_actual) || 0,
        unidad: (newItem.unidad || defaultUnidad).trim() || defaultUnidad,
      };
      // Ambos (insumos y productos) aceptan iva_pct
      payload.iva_pct = parseFloat(newItem.iva_pct) || defaultIvaPct;
      const { data } = await api.post(endpoint, payload);
      toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} "${data.nombre}" creado`);
      await load();
      onSelect({
        id: data.id,
        codigo: data.codigo || "",
        nombre: data.nombre,
        precio_actual: data.precio_actual || 0,
        ultimo_costo: data.ultimo_costo || 0,
        unidad: data.unidad || defaultUnidad,
        iva_pct: data.iva_pct != null ? data.iva_pct : defaultIvaPct,
      });
      setCreating(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            data-testid={testid}
            aria-expanded={open}
            className="flex h-8 w-full items-center justify-between rounded-sm border border-[#E5E5E5] bg-white px-2 text-[12px] hover:bg-neutral-50"
          >
            {displayLabel()}
            <CaretUpDown size={12} className="ml-2 shrink-0 text-[#999]" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[92vw] sm:w-[440px] sm:max-w-[440px] p-0 rounded-sm" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Buscar ${label}…`}
              value={query}
              onValueChange={setQuery}
              className="text-[12px]"
            />
            <CommandList className="max-h-72">
              <CommandEmpty>
                <div className="py-4 px-3 text-center text-[12px] text-[#666] space-y-2">
                  <div>Sin {label}s que matcheen "{query}".</div>
                  <button
                    type="button"
                    data-testid={`combo-create-empty-${kind}`}
                    onClick={openCreate}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-[12px] bg-[#6B1F24] text-white hover:bg-[#571519]"
                  >
                    <Plus size={12} /> Crear "{query || `nuevo ${label}`}"
                  </button>
                </div>
              </CommandEmpty>
              {filtered.length > 0 && (
                <CommandGroup>
                  {filtered.map((p) => {
                    const selected = value?.codigo === p.codigo && value?.nombre === p.nombre;
                    return (
                      <CommandItem
                        key={p.id}
                        value={`${p.codigo}-${p.nombre}`}
                        data-testid={`combo-item-${p.id}`}
                        onSelect={() => {
                          onSelect({
                            id: p.id,
                            codigo: p.codigo || "",
                            nombre: p.nombre,
                            precio_actual: p.precio_actual || 0,
                            ultimo_costo: p.ultimo_costo || 0,
                            unidad: p.unidad || defaultUnidad,
                            iva_pct: p.iva_pct != null ? p.iva_pct : defaultIvaPct,
                          });
                          setOpen(false);
                        }}
                        className="text-[12px] flex items-start gap-2"
                      >
                        <Check
                          size={12}
                          className={cn("mt-1 shrink-0", selected ? "opacity-100" : "opacity-0")}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {p.codigo && (
                              <span className="text-[10px] font-mono text-[#6B1F24] bg-[#F5F1E9] px-1 rounded-sm">
                                {p.codigo}
                              </span>
                            )}
                            <span className="truncate">{p.nombre}</span>
                          </div>
                          <div className="text-[10px] text-[#666] font-mono mt-0.5">
                            Último: ${(p.precio_actual || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                            {p.unidad && ` / ${p.unidad}`}
                            {kind === "productos" && p.veces_vendido > 0 &&
                              ` · vendido ${p.veces_vendido}×`}
                            {kind === "insumos" && p.veces_comprado > 0 &&
                              ` · comprado ${p.veces_comprado}×`}
                          </div>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
            {/* Footer: crear siempre disponible */}
            <div className="border-t border-[#E5E5E5] p-2 bg-neutral-50">
              <button
                type="button"
                data-testid={`combo-create-${kind}`}
                onClick={openCreate}
                className="w-full inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-sm text-[12px] text-[#6B1F24] hover:bg-[#F5F1E9] font-mono uppercase tracking-widest"
              >
                <Plus size={12} weight="bold" /> Crear {label} nuevo
              </button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Dialog de creación rápida */}
      <Dialog open={creating} onOpenChange={(v) => !saving && setCreating(v)}>
        <DialogContent className="max-w-md rounded-sm" data-testid={`combo-create-dialog-${kind}`}>
          <DialogHeader>
            <DialogTitle className="font-heading capitalize">Nuevo {label}</DialogTitle>
            <DialogDescription className="text-[11px] text-[#666]">
              Se agregará al catálogo y quedará seleccionado en la factura.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div>
              <Label className="text-[12px]">Nombre *</Label>
              <Input
                autoFocus
                required
                data-testid="combo-new-nombre"
                className="rounded-sm mt-1"
                value={newItem.nombre}
                onChange={(e) => setNewItem({ ...newItem, nombre: e.target.value })}
                placeholder={`Ej: ${kind === "insumos" ? "Sal fina 25kg" : "Bola de lomo"}`}
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-1">
                <Label className="text-[12px]">Código</Label>
                <Input
                  data-testid="combo-new-codigo"
                  className="rounded-sm mt-1 font-mono text-[12px]"
                  value={newItem.codigo}
                  onChange={(e) => setNewItem({ ...newItem, codigo: e.target.value })}
                  placeholder="auto"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-[12px]">Precio c/IVA</Label>
                <NumberInput
                  step="0.01"
                  data-testid="combo-new-precio"
                  className="rounded-sm mt-1 font-mono text-right"
                  value={newItem.precio_actual}
                  onChange={(e) => setNewItem({ ...newItem, precio_actual: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              {(kind === "insumos" || kind === "productos") && (
                <div className="col-span-1">
                  <Label className="text-[12px]">IVA%</Label>
                  <NumberInput
                    step="0.5"
                    data-testid="combo-new-iva"
                    className="rounded-sm mt-1 font-mono text-right"
                    value={newItem.iva_pct}
                    onChange={(e) => setNewItem({ ...newItem, iva_pct: e.target.value })}
                    placeholder="10.5"
                  />
                </div>
              )}
              <div className={(kind === "insumos" || kind === "productos") ? "col-span-1" : "col-span-2"}>
                <Label className="text-[12px]">Unidad</Label>
                <Input
                  data-testid="combo-new-unidad"
                  className="rounded-sm mt-1 font-mono text-[12px]"
                  value={newItem.unidad}
                  onChange={(e) => setNewItem({ ...newItem, unidad: e.target.value })}
                  placeholder={defaultUnidad}
                />
              </div>
            </div>
            <div className="text-[10px] text-[#666] font-mono">
              El precio se interpreta como <strong>total con IVA</strong>. Si dejás el código en blanco, se generará uno automático (formato MAN-XXXXXX).
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setCreating(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="combo-new-submit"
                className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]"
                disabled={saving}
              >
                {saving ? "Guardando…" : "Crear y seleccionar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
